// Header-level readers for the print formats accepted for ad creatives. They only read the
// bytes they need (never decode pixels), so they're cheap even for very large print files.

export type CreativeFormat = 'jpeg' | 'tiff' | 'png' | 'pdf';

const readBytes = async (file: Blob, start: number, length: number): Promise<Uint8Array> =>
  new Uint8Array(await file.slice(start, start + length).arrayBuffer());

/** Identifies the real format from the file's magic bytes - extensions and MIME types can lie. */
export const sniffCreativeFormat = async (file: Blob): Promise<CreativeFormat | null> => {
  const b = await readBytes(file, 0, 8);
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg';
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47 && b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a) return 'png';
  if ((b[0] === 0x49 && b[1] === 0x49 && b[2] === 0x2a && b[3] === 0x00) ||
      (b[0] === 0x4d && b[1] === 0x4d && b[2] === 0x00 && b[3] === 0x2a)) return 'tiff';
  if (String.fromCharCode(...Array.from(b.subarray(0, 5))) === '%PDF-') return 'pdf';
  return null;
};

export type JpegInfo = { width: number; height: number; components: number };

/**
 * Reads the JPEG frame header (SOFn). `components` is 4 for CMYK/YCCK, 3 for RGB/YCbCr, 1 for
 * grayscale. Returns null for an invalid JPEG, undefined if `b` ends before the frame header.
 */
export const parseJpegInfo = (b: Uint8Array): JpegInfo | null | undefined => {
  if (b[0] !== 0xff || b[1] !== 0xd8) return null;
  let i = 2;
  while (i + 4 <= b.length) {
    if (b[i] !== 0xff) return null;
    const marker = b[i + 1];
    if (marker === 0xff) { i += 1; continue; } // fill byte
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) { i += 2; continue; } // standalone markers
    if (marker === 0xda || marker === 0xd9) return null; // scan/end reached without a frame header
    const isFrame = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isFrame) {
      if (i + 9 >= b.length) return undefined;
      return {
        height: (b[i + 5] << 8) | b[i + 6],
        width: (b[i + 7] << 8) | b[i + 8],
        components: b[i + 9],
      };
    }
    i += 2 + ((b[i + 2] << 8) | b[i + 3]);
  }
  return undefined;
};

// Metadata segments (EXIF, CMYK ICC profiles, Photoshop resources) all precede the frame
// header and comfortably fit in this window; fall back to the whole file only if they don't.
const JPEG_HEADER_WINDOW = 4 * 1024 * 1024;

export const readJpegInfo = async (file: Blob): Promise<JpegInfo | null> => {
  const info = parseJpegInfo(await readBytes(file, 0, JPEG_HEADER_WINDOW));
  if (info !== undefined || file.size <= JPEG_HEADER_WINDOW) return info ?? null;
  return parseJpegInfo(new Uint8Array(await file.arrayBuffer())) ?? null;
};

export type TiffInfo = {
  width: number;
  height: number;
  /** TIFF PhotometricInterpretation - 5 is "Separated" (CMYK when InkSet is 1 or absent). */
  photometric: number;
  inkSet?: number;
};

const TIFF_SHORT = 3;
const TIFF_LONG = 4;

/** Reads the first image directory of a classic (non-Big) TIFF. Null if it can't be read. */
export const readTiffInfo = async (file: Blob): Promise<TiffInfo | null> => {
  try {
    const head = new DataView((await readBytes(file, 0, 8)).buffer);
    const le = head.getUint16(0) === 0x4949;
    if (head.getUint16(2, le) !== 42) return null; // 43 = BigTIFF, unsupported
    const ifdOffset = head.getUint32(4, le);
    const count = new DataView((await readBytes(file, ifdOffset, 2)).buffer).getUint16(0, le);
    const entries = new DataView((await readBytes(file, ifdOffset + 2, count * 12)).buffer);

    const tags = new Map<number, number>();
    for (let e = 0; e < count; e += 1) {
      const o = e * 12;
      const type = entries.getUint16(o + 2, le);
      if (type === TIFF_SHORT) tags.set(entries.getUint16(o, le), entries.getUint16(o + 8, le));
      else if (type === TIFF_LONG) tags.set(entries.getUint16(o, le), entries.getUint32(o + 8, le));
    }
    const width = tags.get(256);
    const height = tags.get(257);
    const photometric = tags.get(262);
    if (!width || !height || photometric == null) return null;
    return { width, height, photometric, inkSet: tags.get(332) };
  } catch {
    return null;
  }
};

export const isCmykTiff = (info: TiffInfo): boolean =>
  info.photometric === 5 && (info.inkSet == null || info.inkSet === 1);
